import { type Locale, locales, parseLocale } from "@repo/fika-shared/src/locale"
import { createMemo, createSignal } from "solid-js"
import { Form } from "@/designSystem/components/form"
import { Select } from "@/designSystem/components/select"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { getLanguageLabel } from "@/lib/i18n"
import { toast } from "@/lib/toast"

export const UserLocaleFragment = graphql(`
  fragment UserLocaleFragment on Me {
    id
    interfaceLocale
  }
`)

export function LocaleForm(props: {
  user: FragmentOf<typeof UserLocaleFragment>
}) {
  const getUser = createFragment(UserLocaleFragment, () => props.user)
  const { LL } = useI18nContext()
  const UpdateCurrentUserMutation = graphql(
    `mutation UpdateCurrentUserLocale($input: UpdateUserInput!) {
      updateCurrentUser(input: $input) {
        ... on MutationUpdateCurrentUserSuccess {
          data {
            ...UserLocaleFragment
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment, UserLocaleFragment],
  )
  const [updateUserState, updateUser] = createMutationWithErrorHandling(
    UpdateCurrentUserMutation,
    "updateCurrentUser",
  )

  const currentLocale = createMemo(() => parseLocale(getUser().interfaceLocale))
  const [interfaceLocale, setInterfaceLocale] = createSignal<Locale>(
    currentLocale(),
  )

  const localeDirty = createMemo(() => currentLocale() !== interfaceLocale())

  const interfaceLocaleOptions = locales
    .map(l => ({
      value: l,
      label: getLanguageLabel(l),
      disabled: false,
    }))
    .sort((a, b) => {
      if (a.value === currentLocale()) return -1
      if (b.value === currentLocale()) return 1
      return a.label.localeCompare(b.label)
    })

  async function handleSubmitLanguage() {
    const newLocale = interfaceLocale()
    if (newLocale === currentLocale()) return

    const state = await updateUser({ input: { interfaceLocale: newLocale } })

    if (state.success) {
      toast.success(LL().settings.informationSaved())
      window.location.reload()
    }
  }

  return (
    <Form
      title={LL().settings.localizationTitle()}
      description={LL().settings.localizationDescription()}
      hue="orange"
      iconName="languages"
      onSubmit={handleSubmitLanguage}
    >
      <Select
        hue="orange"
        label={LL().settings.interfaceLanguageLabel()}
        options={interfaceLocaleOptions}
        value={interfaceLocale}
        onChange={setInterfaceLocale}
      />
      <Form.Save
        hue="orange"
        disabled={!localeDirty()}
        active={updateUserState.fetching}
        label={
          updateUserState.fetching ? LL().common.saving() : LL().common.save()
        }
      />
    </Form>
  )
}
